import styled, { createGlobalStyle } from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/**
 * The print rules.
 *
 * This tool exists to put a bracket on paper, so printing is not an
 * afterthought here, it is the product.
 *
 * ⚠️ THE TRAP THIS REPLACED. The first version hid the site with
 * `visibility: hidden`. That hides pixels but KEEPS LAYOUT, so the document was
 * still its full height and every bracket printed on 19 pages, 18 of them
 * blank. Anything that hides page furniture for print must remove it from
 * layout, which means `display: none`.
 *
 * So the pages are rendered a second time into a container portalled onto
 * `document.body`. That makes "hide everything else" expressible as a single
 * child selector, instead of trying to name every ancestor of a node buried
 * deep in the page.
 *
 * No PDF library is bundled. The browser's own print dialog produces true
 * vector output, lets the reader pick their printer, and has "Save as PDF"
 * built in on every desktop platform.
 */
export const PrintStyles = createGlobalStyle<{
  $sheetW: number;
  $sheetH: number;
  $printW: number;
  $printH: number;
  $margin: number;
}>`
  /* On screen the print-only copy must never be visible or take up space. */
  #bracket-print-root {
    display: none;
  }

  @media print {
    /* Remove the site from the flow entirely, layout included. */
    body > *:not(#bracket-print-root) {
      display: none !important;
    }

    #bracket-print-root {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
    }

    /*
     * Pin the paper. Without a size the browser uses whatever the reader last
     * picked, and the exact-fit promise below would be measured against the
     * wrong sheet.
     */
    @page {
      size: ${({ $sheetW, $sheetH }) => `${$sheetW}mm ${$sheetH}mm`};
      margin: ${({ $margin }) => `${$margin}mm`};
    }

    /*
     * Each page is EXACTLY the printable area, and the SVG inside it is given
     * both dimensions so preserveAspectRatio="xMidYMid meet" can fit the
     * drawing inside. overflow:hidden is the belt to that braces: if a reader
     * overrides the paper in the print dialog, the sheet still cannot bleed
     * onto a second page.
     */
    /*
     * Each page is EXACTLY the printable area, and the SVG inside it is given
     * both dimensions so preserveAspectRatio="xMidYMid meet" can fit the
     * drawing inside. overflow:hidden is the belt to that braces: if a reader
     * overrides the paper in the print dialog, the sheet still cannot bleed
     * onto a second page.
     *
     * Everything here is !important because the node is a CLONE of the on-screen
     * sheet and arrives carrying its screen styling (the paper-proportioned
     * aspect-ratio, the card border, the rounded corners).
     */
    #bracket-print-root .bracket-page {
      width: ${({ $printW }) => `${$printW}mm`} !important;
      height: ${({ $printH }) => `${$printH}mm`} !important;
      aspect-ratio: auto !important;
      max-width: none !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      overflow: hidden !important;
      display: grid !important;
      place-items: center !important;
      background: #fff !important;
      break-inside: avoid;
      page-break-inside: avoid;
      break-after: page;
      page-break-after: always;
    }

    /* The brand credit is PNG-only: one extra line would spill onto a new page. */
    #bracket-print-root .bracket-credit {
      display: none !important;
    }

    /* No trailing blank sheet after the last page. */
    #bracket-print-root .bracket-page:last-child {
      break-after: auto;
      page-break-after: auto;
    }

    #bracket-print-root .bracket-page svg {
      width: 100% !important;
      height: 100% !important;
      min-width: 0 !important;
      display: block !important;
    }

    /* The clone's own wrapper must not add a grid gap or a card around them. */
    #bracket-print-root > * {
      display: block !important;
      gap: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: #fff !important;
    }

    /* Keep the ink cheap. A printed bracket is line art, not a screenshot. */
    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

export const StyledToolHero = styled.section`
  background: url(/assets/hero-bg.webp);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center 0%;
  color: ${CSSVars.white};
  padding: 88px 20px 48px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 20px;
    line-height: 1.15;
    color: ${CSSVars.white};

    .highlight {
      font-weight: 600;
      background: linear-gradient(90deg, #a5a8ff, #ff979c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    max-width: 680px;
    margin: 0 auto;
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.88;
  }

  @media (min-width: ${CSSVars.breakpointMD}) {
    padding: 140px 20px 64px;

    h1 {
      font-size: 2.75rem;
    }

    p {
      font-size: 1.0625rem;
    }
  }
`;

export const StyledToolBody = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px 40px;
`;

export const StyledStudio = styled.div`
  background: ${CSSVars.white};
  border: 1px solid ${CSSVars.gray200};
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(20, 24, 40, 0.08);
  margin-top: -28px;
  overflow: hidden;
  position: relative;
  z-index: 2;
`;

export const StyledStudioHead = styled.div`
  padding: 18px 20px 0;

  h2 {
    font-size: 1.0625rem;
    font-weight: 600;
    margin: 0 0 4px;
    color: ${CSSVars.gray900};
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: ${CSSVars.gray600};
  }
`;

/** Three steps lit from real state, so the tool is never a blank mystery. */
export const StyledToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid ${CSSVars.gray200};

  .spacer {
    flex: 1 1 auto;
  }
`;

export const StyledButton = styled.button<{
  $variant?: 'primary' | 'ghost' | 'accent';
}>`
  appearance: none;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 9px 15px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;

  ${({ $variant }) => {
    if ($variant === 'primary') {
      return `
        background: var(--blue-600);
        border: 1px solid var(--blue-600);
        color: #fff;
        &:hover:not(:disabled) { background: var(--blue-700); border-color: var(--blue-700); }
      `;
    }
    if ($variant === 'accent') {
      return `
        background: #0f7b4f;
        border: 1px solid #0f7b4f;
        color: #fff;
        &:hover:not(:disabled) { background: #0c6742; border-color: #0c6742; }
      `;
    }
    if ($variant === 'ghost') {
      return `
        background: transparent;
        border: 1px solid transparent;
        color: var(--gray-600);
        &:hover:not(:disabled) { background: var(--gray-100); }
      `;
    }
    return `
      background: #fff;
      border: 1px solid var(--gray-300);
      color: var(--gray-800);
      &:hover:not(:disabled) { background: var(--gray-50); }
    `;
  }}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const StyledWorkspace = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;

  /*
   * A grid item defaults to min-width:auto, so it refuses to shrink below its
   * content. The sheet holds an SVG with a 560px min-width, which was dragging
   * the whole grid to 630px on a 390px phone. The studio's overflow:hidden then
   * CLIPPED the controls column rather than scrolling it, so the right-hand end
   * of every field was simply invisible. min-width:0 lets the columns shrink and
   * hands the scrolling back to the sheet, which is the only thing that should
   * scroll sideways.
   */
  > * {
    min-width: 0;
  }

  @media (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: 300px 1fr;
  }
`;

/**
 * The control panel.
 *
 * ⚠️ Spacing comes from ONE scale here, not from per-control margins. The
 * uneven gaps Sergei flagged were the result of each block setting its own
 * bottom margin while some of its children set their own top margin as well, so
 * the distance between two rows depended on which two rows they happened to be.
 */
export const StyledPanel = styled.div`
  --gap-block: 10px;
  --gap-field: 14px;
  --gap-inner: 7px;

  min-width: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: var(--gap-block);
  border-bottom: 1px solid ${CSSVars.gray200};

  @media (min-width: ${CSSVars.breakpointLG}) {
    border-bottom: 0;
    border-right: 1px solid ${CSSVars.gray200};
  }

  /*
   * ⚠️ The selector is .field-label, NOT label.field-label. (No backticks in
   * this comment: it lives inside a JS template literal, where one ends the
   * string and the file stops parsing.)
   *
   * Five of the eight fields label themselves with a <span>, because they are a
   * row of buttons rather than one input and a <label> pointing at nothing is
   * worse than no label. The old element selector missed every one of them, and
   * they inherited white text from the surrounding section, so "Fill the sheet
   * with", "Draw order", "Bracket size", "Sheet background", "Your logo" and
   * "Print style" were rendering white on a white panel: present in the markup,
   * invisible on screen, and picked up only by reading the computed colour.
   */
  .field-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${CSSVars.gray500};
    margin: 0 0 6px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--gap-inner);
  }

  .field[hidden] {
    display: none;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  /* One file-picker button and one quiet destructive link, shared by the
     background and logo groups so they cannot drift apart. */
  .file-btn {
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${CSSVars.gray700};
    background: ${CSSVars.white};
    border: 1px solid ${CSSVars.gray300};
    border-radius: 8px;
    padding: 7px 11px;
    cursor: pointer;

    &:hover {
      border-color: ${CSSVars.gray400};
      background: ${CSSVars.gray50};
    }

    &:focus-within {
      outline: 2px solid ${CSSVars.blue500};
      outline-offset: 1px;
    }

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }
  }

  .link-btn {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${CSSVars.gray600};
    background: none;
    border: 0;
    padding: 4px 2px;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: #a32b22;
    }
  }

  .hint {
    font-size: 0.75rem;
    line-height: 1.5;
    color: ${CSSVars.gray500};
    margin: 0;
  }

  .count {
    font-size: 0.75rem;
    color: ${CSSVars.gray600};
    margin: 0;

    strong {
      color: ${CSSVars.gray900};
    }
  }

  .warn {
    font-size: 0.75rem;
    line-height: 1.5;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    padding: 8px 10px;
    margin: 0;
  }
`;

export const StyledInput = styled.input`
  width: 100%;
  border: 1px solid ${CSSVars.gray300};
  border-radius: 9px;
  padding: 9px 11px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${CSSVars.gray900};
  background: ${CSSVars.white};

  &:focus {
    outline: none;
    border-color: var(--blue-500);
    box-shadow: 0 0 0 3px rgba(59, 108, 255, 0.14);
  }
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 190px;
  border: 1px solid ${CSSVars.gray300};
  border-radius: 9px;
  padding: 10px 11px;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  color: ${CSSVars.gray900};
  background: ${CSSVars.white};

  &:focus {
    outline: none;
    border-color: var(--blue-500);
    box-shadow: 0 0 0 3px rgba(59, 108, 255, 0.14);
  }
`;

export const StyledSegmented = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const StyledSegment = styled.button<{ $active?: boolean }>`
  appearance: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--blue-600)' : 'var(--gray-300)')};
  background: ${({ $active }) => ($active ? 'var(--blue-50)' : '#fff')};
  color: ${({ $active }) => ($active ? 'var(--blue-700)' : 'var(--gray-700)')};

  &:hover {
    border-color: ${({ $active }) => ($active ? 'var(--blue-600)' : 'var(--gray-400)')};
  }
`;

export const StyledCheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 0.875rem;
  color: ${CSSVars.gray800};
  cursor: pointer;

  input {
    margin-top: 2px;
    flex: 0 0 auto;
  }

  span.sub {
    display: block;
    font-size: 0.75rem;
    color: ${CSSVars.gray500};
    margin-top: 2px;
  }
`;

export const StyledStage = styled.div`
  min-width: 0;
  padding: 18px 16px;
  background: ${CSSVars.gray50};
  min-height: 320px;
`;

/**
 * The sheet. This is the only element that survives the print stylesheet, so it
 * carries the white background and the border that reads as paper on screen.
 */
/**
 * The sheet on screen.
 *
 * Each page is drawn at the exact proportions of the chosen paper, so what is
 * on screen is what comes out of the printer. That is the whole point: a
 * printable bracket that surprises you at the printer is a broken tool.
 *
 * Below the tablet breakpoint the paper shape is dropped. A fitted A4 on a
 * 390px phone is about 250px tall and nobody can read it, so the phone gets the
 * drawing at a usable size in a scroll box instead. Printing is unaffected:
 * the print copy is a separate render.
 */
export const StyledSheet = styled.div<{ $ratio: number }>`
  display: grid;
  gap: 14px;

  .bracket-credit {
    border: 1px solid ${CSSVars.gray200};
    border-radius: 8px;
    overflow: hidden;
  }

  .bracket-page {
    background: #fff;
    border: 1px solid ${CSSVars.gray200};
    border-radius: 8px;
    padding: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    svg {
      display: block;
      /* Below this a bracket stops being readable, so it scrolls instead. The
         scroll is confined to this box; the page itself never moves sideways. */
      min-width: 520px;
      width: 100%;
      height: auto;
    }
  }

  @media (min-width: ${CSSVars.breakpointMD}) {
    .bracket-page {
      aspect-ratio: ${({ $ratio }) => $ratio};
      overflow: hidden;
      display: grid;
      place-items: center;
      padding: 12px;

      svg {
        min-width: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
`;

export const StyledEmptyStage = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  padding: 48px 20px;
  color: ${CSSVars.gray500};
  font-size: 0.875rem;

  strong {
    display: block;
    color: ${CSSVars.gray800};
    font-size: 0.9375rem;
    margin-bottom: 4px;
  }
`;

export const StyledRestoredNote = styled.p`
  margin: 0;
  padding: 9px 20px;
  background: #eefaf3;
  border-bottom: 1px solid #cbe9da;
  color: #0f7b4f;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    appearance: none;
    background: none;
    border: 0;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
`;

export const StyledPrivacyNote = styled.p`
  font-size: 0.8125rem;
  color: ${CSSVars.gray500};
  text-align: center;
  margin: 14px 0 0;
`;

/**
 * The handover panel.
 *
 * Every line of copy in here names something the free tool deliberately does
 * not do. That is what keeps it an honest upsell rather than a nag, and it is
 * the test to apply to the next tool and widget pair.
 */
/*
 * The handover panel.
 *
 * ⚠️ Keep this identical to the other nine tools that carry one
 * (`InstagramGridTool`, `TierListTool`, `AlignmentChartTool`, the four schema
 * generators, the story maker and the image resizer). It was a pale blue
 * tinted box here, the only one of the ten not on the house gradient, which is
 * what made this page read as a different site.
 *
 * Structure the markup expects: .bridge-copy (eyebrow, h2, p) on the left,
 * .bridge-action (.bridge-cta, .bridge-note) on the right.
 */
export const StyledBridgePanel = styled.section`
  max-width: 1080px;
  margin: 36px auto 0;
  border-radius: 20px;
  padding: 32px 26px;
  background: linear-gradient(120deg, ${CSSVars.blue500} 0%, #7b5cff 55%, ${CSSVars.pink300} 130%);
  color: ${CSSVars.white};
  text-align: center;
  box-shadow: 0 14px 44px rgba(76, 79, 255, 0.28);

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 44px 42px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    gap: 36px;
  }

  .bridge-copy {
    flex: 1;
  }

  .bridge-eyebrow {
    display: inline-block;
    font-size: 0.75rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 700;
    opacity: 0.85;
    margin-bottom: 8px;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.white};
    margin: 0 0 10px;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    line-height: 1.6;
    font-weight: 300;
    margin: 0 auto;
    max-width: 560px;
    opacity: 0.95;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      margin: 0;
    }
  }

  /* The format caveat on the consolation and triple sheets. Quieter than the
   * body copy, but on the same white so it is never mistaken for a warning. */
  .bridge-caveat {
    margin-top: 12px;
    font-size: ${CSSVars.fontSizeSM};
    line-height: 1.6;
    font-weight: 300;
    opacity: 0.8;
  }

  .bridge-action {
    flex: none;
    margin-top: 24px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      margin-top: 0;
      text-align: center;
    }
  }

  .bridge-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${CSSVars.white};
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeMD};
    padding: 15px 28px;
    border-radius: 999px;
    text-decoration: none;
    white-space: nowrap;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
    transition: transform 0.2s, box-shadow 0.2s;

    /* This one is a real <button>, not an anchor: it stays disabled until the
     * draw has enough players to hand over. The other nine are always live. */
    appearance: none;
    border: 0;
    cursor: pointer;
    font-family: inherit;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 26px rgba(0, 0, 0, 0.24);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      transform: none;
    }
  }

  .bridge-note {
    display: block;
    margin-top: 12px;
    font-size: ${CSSVars.fontSizeSM};
    opacity: 0.82;
    font-weight: 300;
  }
`;

/*
 * The two card strips below follow the free-tools house pattern, copied from
 * `TierListTool.styles.ts`: a centred heading with a subtitle, cards on a
 * 1080px column, 16px radius, a logo or icon tile, and a coloured call to
 * action. They used to be a bespoke left-aligned four-up strip with none of
 * that, which is what made this page read as a different site to every other
 * free tool.
 */
export const StyledWidgetsSection = styled.section`
  max-width: 1080px;
  margin: 60px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    text-align: center;
    margin-bottom: 12px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray600};
    text-align: center;
    max-width: 640px;
    margin: 0 auto 32px;
    line-height: 1.7;
    font-weight: 300;
  }
`;

export const StyledWidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

export const StyledWidgetCard = styled.a`
  display: block;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;

    img {
      width: 48px;
      height: 48px;
      border-radius: 10px;
    }

    h3 {
      font-size: ${CSSVars.fontSizeMD};
      font-weight: 700;
      color: ${CSSVars.black};
      margin: 0;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    margin-bottom: 16px;
    font-weight: 300;
  }

  .widget-cta {
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeSM};
  }
`;

export const StyledRelatedTools = styled.section`
  max-width: 1080px;
  margin: 60px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    text-align: center;
    margin-bottom: 12px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray600};
    text-align: center;
    max-width: 640px;
    margin: 0 auto 32px;
    line-height: 1.7;
    font-weight: 300;
  }
`;

export const StyledRelatedToolGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledRelatedToolCard = styled.div`
  display: block;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  .tool-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(76, 79, 255, 0.1);
    color: ${CSSVars.blue500};
    border-radius: 12px;
    font-size: 24px;
    margin-bottom: 14px;
  }

  /*
   * ⚠️ Set the colour on the h3 ITSELF, not only on a nested <a>. The card is
   * the link now, so there is no inner anchor to catch the rule, and the
   * heading inherits white from the section: the tool names disappeared.
   */
  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 8px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    margin-bottom: 14px;
    font-weight: 300;
  }

  .tool-cta {
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeSM};
  }
`;

/**
 * The handover banner.
 *
 * Styled to the house pattern shared by the other maker tools (tier list,
 * alignment chart, meme generator and the rest of that group): a rounded card
 * in the brand gradient, inset in the same 1080px container the page content
 * uses, rather than a full-bleed band. Kept token-for-token identical to
 * `TierListTool.styles.ts` so the two read as one system, since the two tools
 * sit in the same category and hand over the same way.
 */
export const StyledCtaBanner = styled.section`
  max-width: 1080px;
  margin: 60px auto 0;
  padding: 0 20px;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 0 40px;
  }

  .banner-inner {
    background: linear-gradient(135deg, #4c4fff, #6b8cff);
    border-radius: 20px;
    padding: 40px 28px;
    text-align: center;
    color: ${CSSVars.white};

    h2 {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 14px;
      color: ${CSSVars.white};

      @media screen and (min-width: ${CSSVars.breakpointMD}) {
        font-size: ${CSSVars.fontSize2XL};
      }
    }

    p {
      font-size: ${CSSVars.fontSizeMD};
      max-width: 580px;
      margin: 0 auto 24px;
      line-height: 1.7;
      opacity: 0.92;
      font-weight: 300;
    }

    .banner-cta {
      display: inline-block;
      background: ${CSSVars.white};
      color: ${CSSVars.blue500};
      padding: 12px 24px;
      border-radius: 10px;
      font-weight: 700;
      text-decoration: none;
      transition: transform 0.2s;

      &:hover {
        transform: translateY(-2px);
      }
    }

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 60px 40px;
    }
  }
`;


/**
 * The four formats as links.
 *
 * Replaced the Format switcher in the control panel. A card per page rather
 * than a bare list of names because the names do not tell anybody which one
 * they want: the difference that matters to somebody holding a printer is how
 * many sheets come out, so that is on the card.
 */
export const StyledFormatGrid = styled.section`
  margin: 26px 0 0;
  padding: 20px;
  background: ${CSSVars.white};
  border: 1px solid ${CSSVars.gray200};
  border-radius: 14px;

  h2 {
    font-size: 1.0625rem;
    font-weight: 700;
    color: ${CSSVars.gray900};
    margin: 0 0 4px;
  }

  > p {
    font-size: 0.8125rem;
    color: ${CSSVars.gray600};
    margin: 0 0 14px;
    max-width: 62ch;
  }

  h3 {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${CSSVars.gray500};
    margin: 20px 0 9px;
  }

  h3:first-of-type {
    margin-top: 4px;
  }

  ul.sheets {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 10px;
  }

  ul.sheets li {
    display: flex;
  }

  ul.sheets a,
  ul.sheets .current {
    font-size: 0.875rem;
    line-height: 1;
    padding: 9px 13px;
    border-radius: 99px;
    border: 1px solid ${CSSVars.gray200};
    background: ${CSSVars.gray50};
    color: ${CSSVars.gray700};
    text-decoration: none;
    white-space: nowrap;
  }

  ul.sheets a:hover {
    border-color: ${CSSVars.blue300};
    background: ${CSSVars.blue50};
    color: ${CSSVars.blue700};
  }

  ul.sheets a:focus-visible {
    outline: 2px solid ${CSSVars.blue500};
    outline-offset: 1px;
  }

  ul.sheets .current {
    border-color: ${CSSVars.gray900};
    background: ${CSSVars.white};
    color: ${CSSVars.gray900};
    font-weight: 700;
  }

  .here {
    margin: 18px 0 0;
    padding-top: 14px;
    border-top: 1px solid ${CSSVars.gray200};
    font-size: 0.8125rem;
    color: ${CSSVars.gray600};
  }

`;

/** Background swatches. The image picker beside them is a shared `.file-btn`. */
export const StyledSwatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;

  .swatch {
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 7px;
    border: 1px solid ${CSSVars.gray300};
    cursor: pointer;

    &.on {
      /* An outline rather than a border, so picking a swatch never moves the
         row by a pixel. */
      outline: 2px solid ${CSSVars.blue500};
      outline-offset: 1px;
    }

    &:focus-visible {
      outline: 2px solid ${CSSVars.blue500};
      outline-offset: 1px;
    }
  }
`;

/** Logo picker: choose a file, see what you chose, remove it. */
export const StyledLogoField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  .preview {
    width: 52px;
    height: 34px;
    object-fit: contain;
    border: 1px solid ${CSSVars.gray200};
    border-radius: 7px;
    background: ${CSSVars.gray50};
    padding: 3px;
  }
`;

/**
 * A folded control group.
 *
 * The header doubles as the summary: it names the group and reports the value
 * the group is currently set to, so folding a group never hides what it does.
 */
export const StyledGroup = styled.section`
  border: 1px solid ${CSSVars.gray200};
  border-radius: 10px;
  background: ${CSSVars.white};
  overflow: hidden;

  .head {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 13px;
    background: none;
    border: 0;
    cursor: pointer;
    text-align: left;
    font-family: inherit;

    &:hover {
      background: ${CSSVars.gray50};
    }

    &:focus-visible {
      outline: 2px solid ${CSSVars.blue500};
      outline-offset: -2px;
    }
  }

  .title {
    font-size: 0.875rem;
    font-weight: 700;
    color: ${CSSVars.gray900};
  }

  .value {
    margin-left: auto;
    font-size: 0.75rem;
    color: ${CSSVars.gray500};
    text-align: right;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chev {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-right: 1.5px solid ${CSSVars.gray400};
    border-bottom: 1.5px solid ${CSSVars.gray400};
    transform: rotate(45deg) translate(-2px, -2px);
    transition: transform 0.18s ease;
  }

  .head[aria-expanded='true'] .chev {
    transform: rotate(225deg) translate(-2px, -2px);
  }

  .head[aria-expanded='true'] .value {
    opacity: 0;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: var(--gap-field);
    padding: 2px 13px 15px;
  }

  .body[hidden] {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .chev {
      transition: none;
    }
  }
`;

/**
 * The one thing the old paper bar said that was worth keeping.
 *
 * It reported "Fits 1 A4 page exactly" on every sheet that fits, which is
 * almost all of them and therefore almost never news. The case that IS news is
 * a 64-player draw: it fits, but it prints at about 5pt, and somebody deserves
 * to know that before they walk to the printer.
 */
export const StyledFitWarning = styled.p`
  margin: 0;
  padding: 10px 16px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #92400e;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
`;
